import { useCallback, useEffect, useState } from "react";
import { Link, Route, Routes, useNavigate, useParams } from "react-router-dom";
import { Eye, LogOut, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errMsg, fmtTime } from "@/lib/api";
import { card, Empty, inputCls } from "@/components/admin/kit";
import { QuickEnrol } from "./QuickEnrol";
import { MySubmissions } from "./MySubmissions";
import { OrgAdminPages, OrgHome, PatientActions } from "./OrgExtras";

function useGet(api, path) {
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const load = useCallback(() => { api.get(path).then((r) => { setData(r.data); setErr(""); }).catch((e) => setErr(errMsg(e))); }, [api, path]);
  useEffect(() => { load(); }, [load]);
  return [data, err];
}

function PatientList({ api, base }) {
  const [q, setQ] = useState("");
  const [list] = useGet(api, `/org/patients?q=${encodeURIComponent(q)}`);
  return (
    <div className={card} data-testid="org-patients">
      <input className={`${inputCls} mb-3`} placeholder="Search patients" value={q} onChange={(e) => setQ(e.target.value)} data-testid="org-patient-search" />
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No patients.</Empty> : list.map((p) => (
        <Link key={p.id} to={`${base}/patients/${p.id}`} className="flex items-center gap-2 border-b border-[var(--lav-2)] py-2 text-sm hover:text-[var(--violet)]" data-testid={`org-patient-${p.id}`}>
          <b>{p.name}</b><span className="text-muted-foreground">{p.age} yrs · calls to {p.call_receiver === "caregiver" ? "family member" : "patient"}</span>
          {p.calls_stopped && <span className="ml-auto rounded-full bg-red-100 px-2 text-xs font-bold text-red-700">calls stopped</span>}
        </Link>
      ))}
    </div>
  );
}

function PatientView({ api, me, base }) {
  const { pid } = useParams();
  const [d, err] = useGet(api, `/org/patients/${pid}`);
  if (err) return <div className={card} data-testid="org-patient-refused"><p className="font-bold text-red-700">{err === "Not found" ? "Not found — this patient is not enrolled at your organisation." : err}</p></div>;
  if (!d) return <Empty>Loading…</Empty>;
  return (
    <div className={card} data-testid="org-patient-detail">
      <p className="text-xl font-bold" data-testid="org-patient-name">{d.patient.name}</p>
      <p className="text-sm text-muted-foreground">{d.patient.age} yrs · consent {d.consent ? `given ${fmtTime(d.consent_at)}` : "not recorded"}</p>
      <p className="mt-4 font-bold">Medicines from your organisation</p>
      {!d.medicines.length ? <Empty>No medicines.</Empty> : d.medicines.map((m) => (
        <p key={m.id} className="border-b border-[var(--lav-2)] py-2 text-sm" data-testid={`org-med-${m.id}`}><b>{m.name}</b> · <span className="font-semibold text-[var(--violet)]" data-testid={`org-med-purpose-${m.id}`}>{m.purpose}</span>{m.frequency && m.frequency.type !== "daily" ? ` · ${({ weekly: "once a week", alternate: "alternate days", days: "specific days" })[m.frequency.type]}${(m.frequency.weekdays || []).length ? ` (${m.frequency.weekdays.map((d) => ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"][d]).join(", ")})` : ""}` : ""} · {m.form} · {m.dose_amount} · {m.slots.join(", ")} · {({ after: "after food", before: "before food", empty_stomach: "empty stomach", bedtime: "bedtime", none: "any time" })[m.food] || m.food}{m.doctor ? ` · ${m.doctor}` : ""}</p>
      ))}
      <PatientActions api={api} pid={pid} me={me} base={base} />
    </div>
  );
}

function Calls({ api }) {
  const [list] = useGet(api, "/org/calls");
  return (
    <div className={card} data-testid="org-calls">
      {!list ? <Empty>Loading…</Empty> : !list.length ? <Empty>No calls yet.</Empty> : list.map((c) => (
        <p key={c.id} className="border-b border-[var(--lav-2)] py-2 text-sm"><b>{fmtTime(c.created_at)}</b> · {c.patient} · {c.kind}{c.slot ? ` · ${c.slot}` : ""} · {c.result}{c.listened_fully ? " · listened fully" : ""}</p>
      ))}
    </div>
  );
}

function Report({ api }) {
  const [r] = useGet(api, "/org/report");
  return (
    <div className="grid gap-3 sm:grid-cols-4" data-testid="org-report">
      {r && [["Patients", r.patients], ["Calls", r.calls], ["Answered", r.answered], ["Listened fully", r.listened_fully]].map(([k, v]) => (
        <div key={k} className={card}><p className="text-xs text-muted-foreground">{k}</p><p className="text-2xl font-bold">{v}</p></div>
      ))}
    </div>
  );
}

export function OrgPortal({ api, base, banner, onLogout }) {
  const [me, err] = useGet(api, "/org/me");
  const nav = useNavigate();
  if (err) return <div className="p-8"><p className="font-bold text-red-700" data-testid="org-error">{err}</p>{onLogout && <Button className="mt-3" onClick={onLogout}>Log in again</Button>}</div>;
  if (!me) return <Empty>Loading…</Empty>;
  const writer = ["front_desk", "org_admin"].includes(me.role);
  const adm = me.role === "org_admin" || me.read_only;
  const tabs = [["", "Home"], ["/patients", me.role === "doctor" ? "My patients" : "Patients"], ...(writer || me.read_only ? [["/submissions", "Submissions"]] : []), ["/calls", "Call log"],
    ...(adm || me.role === "doctor" ? [["/reports", "Reports"]] : []), ...(adm ? [["/staff", "Staff & doctors"], ["/settings", "Settings"], ["/billing", "Billing"], ["/audit", "Audit log"]] : [])];
  return (
    <div className="mx-auto max-w-5xl p-4 sm:p-6" data-testid="org-portal">
      {me.read_only && <div className="mb-4 flex items-center gap-2 rounded-2xl bg-orange-100 px-4 py-3 text-sm font-semibold text-orange-800" data-testid="view-as-banner"><Eye size={16} /> Viewing as {me.org.name} — read-only. {banner}<a href="/admin/hospitals" className="ml-auto underline" data-testid="view-as-exit">Exit view as</a></div>}
      {me.org.status === "suspended" && <div className="mb-4 rounded-2xl bg-red-100 px-4 py-3 text-sm font-semibold text-red-700" data-testid="org-suspended-banner">This organisation is suspended — no reminder calls are going out.</div>}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <div><p className="text-2xl font-bold" data-testid="org-name">{me.org.name}</p><p className="text-xs text-muted-foreground">{me.name} · {me.role.replace("_", " ")} · {me.org.status}{me.org.trial_end ? ` until ${me.org.trial_end}` : ""}</p></div>
        <div className="ml-auto flex flex-wrap gap-2">
          {tabs.map(([p, l]) => <Button key={l} variant="outline" size="sm" onClick={() => nav(`${base}${p}`)} data-testid={`org-tab-${l.toLowerCase().replace(/[^a-z]+/g, "-")}`}>{l}</Button>)}
          {onLogout && <Button variant="ghost" size="sm" onClick={onLogout} data-testid="org-logout-btn"><LogOut size={14} /> Log out</Button>}
        </div>
      </div>
      <Routes>
        <Route path="/" element={<OrgHome api={api} base={base} me={me} />} />
        <Route path="patients" element={<PatientList api={api} base={base} />} />
        <Route path="enrol" element={writer ? <QuickEnrol api={api} org={me.org} onDone={() => nav(`${base}/submissions`)} /> : <Empty>Only front desk staff can enrol.</Empty>} />
        <Route path="submissions" element={<MySubmissions api={api} base={base} canWrite={writer} />} />
        <Route path="patients/:pid" element={<PatientView api={api} me={me} base={base} />} />
        <Route path="calls" element={<Calls api={api} />} />
        <Route path="report" element={<Report api={api} />} />
        {Object.entries(OrgAdminPages).map(([k, render]) => <Route key={k} path={k} element={(k === "reports" ? adm || me.role === "doctor" : adm) ? render(api, me) : <Empty>Only the organisation admin can open this.</Empty>} />)}
      </Routes>
    </div>
  );
}
